/**
 * agentOptimize — Agentic resume optimization engine.
 *
 * Two-phase approach:
 *   Phase 1 (Score):   ATS score of raw profile against JD  (quick baseline)
 *   Phase 2 (Optimize): Rewrite resume to maximise JD match  (full optimization)
 *
 * Model priority:
 *   1. Local Ollama (VITE_OLLAMA_URL / VITE_OLLAMA_MODEL) — fast, private
 *   2. `ai-companion` Supabase Edge Function (Groq) — cloud fallback
 *
 * No Pinecone is needed here — the scoring in phase 1 is keyword / semantic
 * based on the raw text. The Pinecone semantic scores come from explain-job-match.
 */

import { supabase } from '../../lib/supabase';
import { getAgentResponseLanguage } from './utils/agentLocale';

// ── Config ────────────────────────────────────────────────────────────────────

const OLLAMA_URL   = (import.meta.env.VITE_OLLAMA_URL   as string | undefined) ?? 'http://localhost:11434';
const OLLAMA_MODEL = (import.meta.env.VITE_OLLAMA_MODEL as string | undefined) ?? 'llama3.2:3b';

const JSON_GUARD = [
  'OUTPUT REQUIREMENTS:',
  '- Return ONLY the JSON object specified below.',
  '- Do NOT include markdown code fences, explanations, prefaces, or trailing text.',
  '- The first character of your response MUST be { and the last MUST be }.',
].join('\n');

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ScoreResult {
  atsScore:      number;      // 0-100
  keywordHits:   string[];
  keywordMisses: string[];
  gapAnalysis:   string;
}

export interface OptimizeResult {
  optimizedResume:    string;
  matchScoreBefore:   number;   // 0-100  (from score phase)
  matchScoreAfter:    number;   // 0-100  (from optimize phase)
  matchedSkills:      string[];
  missingSkills:      string[];
  improvements:       string[];
  coverLetterStarter: string;   // 2-sentence opener
}

export interface OptimizeJobContext {
  id:          string;
  title:       string;
  company:     string;
  jdText:      string;   // assembled from description_md + skills
  /** External listing URL — surfaced to the canvas Apply button so it can
   *  open the real job posting (LinkedIn / Naukri / Peerlist) in a new tab.
   *  Was missing before, which is why Apply did nothing for external jobs. */
  url?:        string | null;
  location?:   string | null;
  sourceChannel?: string | null;
}

/** True when the value is a real http(s) listing URL (not '#', blank, etc.). */
export function isValidListingUrl(url: string | null | undefined): url is string {
  if (!url || typeof url !== 'string') return false;
  const t = url.trim();
  if (!t || t === '#' || t === 'about:blank' || /^javascript:/i.test(t)) return false;
  return /^https?:\/\//i.test(t);
}

/**
 * Resolve the best apply/open URL from every place we might have stored it.
 * The state-machine optimize path historically only put title/company on
 * canvas.state.job — never url — so we also check canvasJob, the jobs panel,
 * and any saved job_applications.job_url.
 */
export function resolveJobListingUrl(sources: {
  canvasJob?: { url?: string | null; jobUrl?: string | null } | null;
  canvasStateJob?: { url?: string | null; jobUrl?: string | null } | null;
  panelJob?: { url?: string | null } | null;
  savedJobUrl?: string | null;
}): string | undefined {
  const candidates = [
    sources.canvasJob?.url,
    sources.canvasJob?.jobUrl,
    sources.canvasStateJob?.url,
    sources.canvasStateJob?.jobUrl,
    sources.panelJob?.url,
    sources.savedJobUrl,
  ];
  for (const c of candidates) {
    if (isValidListingUrl(c)) return c.trim();
  }
  return undefined;
}

// ── Prompt builders ───────────────────────────────────────────────────────────

function buildScorePromptText(resume: string, jd: string): string {
  return [
    'Score the resume against the job description.',
    '',
    'RESUME:\n---\n' + resume.slice(0, 3_000) + '\n---',
    '',
    'JOB DESCRIPTION:\n---\n' + jd.slice(0, 2_000) + '\n---',
    '',
    'Return a single JSON object with EXACTLY these keys:',
    '{ "ats_score": number, "keyword_hits": string[], "keyword_misses": string[], "gap_analysis": string }',
    '',
    JSON_GUARD,
  ].join('\n');
}

function buildOptimizePromptText(resume: string, jobTitle: string, company: string, jd: string): string {
  return [
    `Optimize the resume for the role: ${jobTitle} at ${company}.`,
    '',
    'RULES:',
    '- Preserve every employer, job title, education entry, and certification.',
    '- Reword bullets with strong action verbs; quantify outcomes ONLY where numbers already exist.',
    '- Weave keywords from the JD naturally into Summary, Skills, and Experience.',
    '- Return plain text resume (no markdown).',
    '',
    'RESUME:\n---\n' + resume.slice(0, 3_000) + '\n---',
    '',
    'JOB DESCRIPTION:\n---\n' + jd.slice(0, 2_000) + '\n---',
    '',
    'Return a single JSON object with EXACTLY these keys:',
    '{',
    '  "optimized_resume": string,',
    '  "match_score": number,       // 0-100, honest estimate',
    '  "matched_skills": string[],  // skills candidate has that JD requires',
    '  "missing_skills": string[],  // skills JD needs that candidate lacks',
    '  "improvements": string[],    // 3-5 specific changes made',
    '  "cover_letter_starter": string // 2-sentence compelling opener for a cover letter',
    '}',
    '',
    JSON_GUARD,
  ].join('\n');
}

// ── Low-level Ollama call (non-streaming, returns full JSON) ──────────────────

async function callOllamaJson<T extends Record<string, unknown>>(
  systemPrompt: string,
  userPrompt: string,
  signal?: AbortSignal,
): Promise<T | null> {
  try {
    const res = await fetch(`${OLLAMA_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        model:   OLLAMA_MODEL,
        stream:  false,
        options: { temperature: 0.2, num_predict: 3000 },
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user',   content: userPrompt   },
        ],
      }),
    });
    if (!res.ok) return null;
    const payload = (await res.json()) as { message?: { content?: string } };
    const raw = payload.message?.content ?? '';
    // Model may wrap JSON in ```json ... ```
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    return JSON.parse(match[0]) as T;
  } catch {
    return null;
  }
}

// ── Low-level Groq fallback via ai-companion edge function ────────────────────

async function callGroqJson<T extends Record<string, unknown>>(
  systemPrompt: string,
  userPrompt: string,
  signal?: AbortSignal,
): Promise<T | null> {
  try {
    // ai-companion doesn't accept AbortSignal, so we race with a timeout
    const timeout = new Promise<null>((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 30_000),
    );
    const invoke = supabase.functions.invoke<{ content?: string }>('ai-companion', {
      body: {
        message:           userPrompt,
        history:           [],
        user_context:      systemPrompt,
        response_language: getAgentResponseLanguage(),
        assistant_gender:  'female',
      },
    });

    const result = await Promise.race([invoke, timeout as Promise<never>]);
    if (!('data' in result) || !result.data?.content) return null;

    const raw = result.data.content;
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    return JSON.parse(match[0]) as T;
  } catch {
    return null;
  }
}

// ── Phase 1: ATS score ────────────────────────────────────────────────────────

const SCORE_SYSTEM = [
  'You are an ATS scoring engine. Compare a resume to a job description.',
  'Return strict JSON only. Never invent data.',
].join(' ');

export async function scoreResume(
  resume: string,
  jd:     string,
  signal?: AbortSignal,
): Promise<ScoreResult> {
  const userPrompt = buildScorePromptText(resume, jd);

  interface Raw { ats_score?: unknown; keyword_hits?: unknown; keyword_misses?: unknown; gap_analysis?: unknown }

  const toArr = (v: unknown): string[] =>
    Array.isArray(v) ? (v as unknown[]).map(String).filter(Boolean).slice(0, 20) : [];
  const toScore = (v: unknown, fb = 30) => {
    const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : fb;
  };

  const raw =
    await callOllamaJson<Raw>(SCORE_SYSTEM, userPrompt, signal) ??
    await callGroqJson<Raw>(SCORE_SYSTEM, userPrompt, signal);

  if (!raw) {
    return { atsScore: 35, keywordHits: [], keywordMisses: [], gapAnalysis: 'Could not compute score.' };
  }
  return {
    atsScore:      toScore(raw.ats_score, 35),
    keywordHits:   toArr(raw.keyword_hits),
    keywordMisses: toArr(raw.keyword_misses),
    gapAnalysis:   String(raw.gap_analysis ?? ''),
  };
}

// ── Phase 2: Optimize ─────────────────────────────────────────────────────────

const OPTIMIZE_SYSTEM = [
  'You are a senior ATS-aware resume optimizer.',
  'Rewrite resumes to maximise match with the target JD.',
  'NEVER fabricate skills, employers, achievements, metrics, or credentials.',
  'Return strict JSON only.',
].join(' ');

export async function optimizeResume(
  resume:   string,
  job:      OptimizeJobContext,
  scoreBefore: number,
  signal?:  AbortSignal,
): Promise<OptimizeResult> {
  const userPrompt = buildOptimizePromptText(resume, job.title, job.company, job.jdText);

  interface Raw {
    optimized_resume?: unknown;
    match_score?: unknown;
    matched_skills?: unknown;
    missing_skills?: unknown;
    improvements?: unknown;
    cover_letter_starter?: unknown;
  }

  const toArr = (v: unknown): string[] =>
    Array.isArray(v) ? (v as unknown[]).map(String).filter(s => s && s.length < 60 && !s.toLowerCase().includes('unavailable')).slice(0, 10) : [];
  const toScore = (v: unknown, fb: number) => {
    const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : fb;
  };

  const raw =
    await callOllamaJson<Raw>(OPTIMIZE_SYSTEM, userPrompt, signal) ??
    await callGroqJson<Raw>(OPTIMIZE_SYSTEM, userPrompt, signal);

  if (!raw) {
    return {
      optimizedResume:    resume,
      matchScoreBefore:   scoreBefore,
      matchScoreAfter:    Math.min(100, scoreBefore + 15),
      matchedSkills:      [],
      missingSkills:      [],
      improvements:       ['Optimization service unavailable — try again shortly.'],
      coverLetterStarter: '',
    };
  }

  return {
    optimizedResume:    String(raw.optimized_resume ?? resume),
    matchScoreBefore:   scoreBefore,
    matchScoreAfter:    toScore(raw.match_score, Math.min(100, scoreBefore + 20)),
    matchedSkills:      toArr(raw.matched_skills),
    missingSkills:      toArr(raw.missing_skills),
    improvements:       toArr(raw.improvements),
    coverLetterStarter: String(raw.cover_letter_starter ?? ''),
  };
}

// ── JD builder from AgentJob fields ──────────────────────────────────────────

export function buildJdFromJob(job: {
  description_md?: string | null;
  skills?: string[] | null;
  must_have_skills?: string[] | null;
  good_to_have_skills?: string[] | null;
  experience_level?: string | null;
  job_type?: string | null;
  location?: string | null;
  remote?: boolean | null;
}): string {
  const parts: string[] = [];
  if (job.experience_level) parts.push(`Experience Level: ${job.experience_level}`);
  if (job.job_type)         parts.push(`Job Type: ${job.job_type}`);
  if (job.remote)           parts.push('Location: Remote');
  else if (job.location)    parts.push(`Location: ${job.location}`);

  const mustHave = job.must_have_skills ?? job.skills ?? [];
  if (mustHave.length) parts.push(`Required Skills: ${mustHave.slice(0, 20).join(', ')}`);

  const niceToHave = job.good_to_have_skills ?? [];
  if (niceToHave.length) parts.push(`Nice-to-have: ${niceToHave.slice(0, 10).join(', ')}`);

  if (job.description_md) parts.push('', job.description_md.slice(0, 2_500));

  return parts.join('\n');
}
